import React, { Component } from 'react'
import "../../css/general-style.css"
import { BsJournals } from "react-icons/bs"
import { AiOutlineSearch, AiOutlineWarning } from "react-icons/ai"
import DashModalV2 from '../DashModalV2'
import { getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import { pricesFormat } from '../../functions/pricesFormat'

const SEARCH_DEBOUNCE_MS = 500
const PAGE_SIZE = 60

const money = (v) => (v === null || v === undefined ? '—' : '$' + pricesFormat(v))

/**
 * PRODINFOV3-2026-10-18 — "Browse Catalogue" for Products Information V3 (pages/InventoryV3.js).
 *
 * Replaces components/Catalogue2.0/Catalogue2NoPrices.js (left untouched), which loaded the whole Sage
 * /Items/get list and showed Sage's price. This one reads /v3/inventory/products (RDS, Price Management
 * prices levels 1-3, searched/filtered/paged on the server) and /v3/inventory/product-lines.
 * Same permission rules as before: the special salesperson '0010' sees the COST instead of prices
 * (the backend only sends `cost` to users allowed to see it, so this is display logic only).
 * Picking a card calls Padre.selectProduct(row) and closes the modal.
 */
export default class ProductBrowserV3 extends Component {
    constructor(props) {
        super(props)
        this._searchTimer = null
        this._seq = 0
    }

    state = {
        showModal: false,
        loading: false,
        loadError: '',
        products: [],
        total: 0,
        page: 1,
        search: '',
        productLines: [],
        selectedLine: '',
    }

    componentWillUnmount() {
        if (this._searchTimer) clearTimeout(this._searchTimer)
    }

    open = async () => {
        await this.setState({ showModal: true })
        if (this.state.productLines.length === 0) this.fetchProductLines()
        this.fetchProducts()
    }

    close = () => this.setState({ showModal: false })

    async fetchProductLines() {
        try {
            const resp = await getInformationWithDataV3('/v3/inventory/product-lines', { idCompany: Number(getValueCookie('CompanyId')) })
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ productLines: resp.data })
            }
        } catch (error) {
            console.error('ProductBrowserV3 product lines failed:', error)
        }
    }

    async fetchProducts() {
        const seq = ++this._seq
        this.setState({ loading: true, loadError: '' })
        let products = []
        let total = 0
        let loadError = ''
        try {
            const resp = await getInformationWithDataV3('/v3/inventory/products', {
                idCompany: Number(getValueCookie('CompanyId')),
                search: this.state.search || undefined,
                productLine: this.state.selectedLine || undefined,
                page: this.state.page,
                pageSize: PAGE_SIZE,
            })
            if (resp && resp.status && resp.status.code === 1) {
                products = resp.data || []
                total = resp.total || 0
            } else {
                loadError = 'The catalogue could not be loaded. Please try again.'
            }
        } catch (error) {
            console.error('ProductBrowserV3 products failed:', error)
            loadError = 'The catalogue could not be loaded. Please try again.'
        }
        if (seq !== this._seq) return // a newer request is in flight — ignore this answer
        this.setState({ loading: false, products: products, total: total, loadError: loadError })
    }

    onSearchChange = (e) => {
        const value = e.target.value
        this.setState({ search: value })
        if (this._searchTimer) clearTimeout(this._searchTimer)
        this._searchTimer = setTimeout(() => {
            this.setState({ page: 1 }, () => this.fetchProducts())
        }, SEARCH_DEBOUNCE_MS)
    }

    onLineChange = (e) => {
        this.setState({ selectedLine: e.target.value, page: 1 }, () => this.fetchProducts())
    }

    goToPage(page) {
        this.setState({ page: page }, () => this.fetchProducts())
    }

    pick(row) {
        this.props.Padre.selectProduct(row)
        this.close()
    }

    render() {
        const { showModal, loading, loadError, products, total, page, search, productLines, selectedLine } = this.state
        const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
        const costInsteadOfPrice = getValueCookie('SalesPerson') === '0010'

        return (
            <React.Fragment>
                <button hidden={this.props.hidden} id={this.props.id} disabled={this.props.disabled} type="button" onClick={this.open}
                    className="btn purpleButton btn-lg">
                    <BsJournals /> {this.props.titleButton}
                </button>

                <DashModalV2
                    title="Inventory Catalogue"
                    subtitle="Prices come from Price Management for the current company"
                    show={showModal}
                    onClose={this.close}
                    size="xl"
                >
                    {/* Search + product line */}
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                        <div style={{ flex: 1, minWidth: '240px', position: 'relative' }}>
                            <AiOutlineSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                            <input type="text" autoComplete="off" value={search} onChange={this.onSearchChange}
                                placeholder="Search by code, description, UPC or product line..."
                                style={{ width: '100%', padding: '0.6rem 0.875rem 0.6rem 2.25rem', border: '1.5px solid #E2E8F0', borderRadius: '8px', fontSize: '0.9375rem', outline: 'none' }} />
                        </div>
                        <select value={selectedLine} onChange={this.onLineChange}
                            style={{ minWidth: '220px', padding: '0.6rem 0.875rem', border: '1.5px solid #E2E8F0', borderRadius: '8px', fontSize: '0.9375rem', background: 'white' }}>
                            <option value="">All Product Lines</option>
                            {productLines.map((line) => (
                                <option key={line.ProductLine} value={line.ProductLine}>{line.ProductLineName}</option>
                            ))}
                        </select>
                    </div>

                    <p style={{ fontSize: '0.8rem', color: '#64748B', margin: '0 0 0.75rem' }}>
                        {loading ? 'Loading...' : total + ' product' + (total === 1 ? '' : 's')}
                    </p>
                    {loadError !== '' && (
                        <p style={{ fontSize: '0.85rem', fontWeight: 700, color: '#991B1B', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <AiOutlineWarning /> {loadError}
                        </p>
                    )}

                    <div className="row g-2">
                        {!loading && products.length === 0 && loadError === '' && (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>No products found</div>
                        )}
                        {products.map((row) => (
                            <div key={row.itemCode} className="col-sm-12 col-md-6 col-lg-4">
                                <div role="button" tabIndex={0} onClick={() => this.pick(row)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.pick(row) } }}
                                    style={{ background: '#ffffff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', marginBottom: '10px', display: 'flex', gap: '12px', alignItems: 'center', cursor: 'pointer' }}>
                                    <img src={row.itemURLImage || '/assets/image-not-available.png'} alt={row.itemCode}
                                        style={{ width: '84px', height: '84px', objectFit: 'contain', borderRadius: '8px', background: '#F8FAFC', flexShrink: 0, border: '1px solid #F1F5F9', padding: '4px' }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                            <span style={{ background: 'rgba(37,99,235,0.08)', color: '#1e40af', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>{row.itemCode}</span>
                                            <span style={{ background: 'rgba(16,185,129,0.08)', color: '#065F46', border: '1px solid #A7F3D0', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>On hand: {row.quantity || 0}</span>
                                        </div>
                                        <p style={{ fontSize: '0.85rem', margin: '0 0 6px', color: '#0F172A' }}>{row.completeDesc || row.abbreviatedDesc}</p>
                                        {costInsteadOfPrice ? (
                                            <p style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F172A', margin: 0 }}>Cost: {money(row.cost)}</p>
                                        ) : row.hasPricing ? (
                                            <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0F172A', margin: 0 }}>
                                                L1 {money(row.level1Price)} &middot; L2 {money(row.level2Price)} &middot; L3 {money(row.level3Price)}
                                            </p>
                                        ) : (
                                            <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#92400E', display: 'flex', alignItems: 'center', gap: '4px', margin: 0 }}>
                                                <AiOutlineWarning /> No price in Price Management
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                        <div className="btn-group btn-group-lg">
                            <button type="button" disabled={page <= 1 || loading} onClick={() => this.goToPage(page - 1)} className="btn blueButton">Prev</button>
                            <span style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', color: '#64748B' }}>Page {page} of {totalPages}</span>
                            <button type="button" disabled={page >= totalPages || loading} onClick={() => this.goToPage(page + 1)} className="btn blueButton">Next</button>
                        </div>
                    </div>
                </DashModalV2>
            </React.Fragment>
        )
    }
}
